<template>
  <wui-modal
    :title="t('chat.textFormatLinkTitle')"
    :size="40"
    :closable="true"
    :class="modalClass"
    data-test="chat-text-format-link-dialog"
  >
    <div class="flex flex-col gap-4">
      <wui-input
        v-model="linkText"
        :placeholder="t('chat.textFormatLinkTextPlaceholder')"
        with-clear
        data-test="chat-text-format-link-text"
        @keydown.enter.prevent="onTextEnter"
      />
      <wui-input
        v-model="linkUrl"
        :placeholder="t('chat.textFormatLinkUrlPlaceholder')"
        with-clear
        data-test="chat-text-format-link-url"
        @keydown.enter.prevent="submit"
      />
    </div>

    <template #footer>
      <wui-btn
        v-if="isEditing"
        class="whitespace-nowrap"
        :size="40"
        variant="negative"
        state="alpha"
        data-test="chat-text-format-link-remove"
        @click="removeLink"
      >
        {{ t('chat.textFormatLinkRemove') }}
      </wui-btn>
      <div class="wui-modal__spacer" />
      <wui-btn
        :size="40"
        variant="neut"
        state="alpha"
        data-test="chat-text-format-link-cancel"
        @click="close(false)"
      >
        {{ t('common.cancel') }}
      </wui-btn>
      <wui-btn
        :size="40"
        variant="brand"
        state="filled"
        :disabled="!canSubmit"
        data-test="chat-text-format-link-submit"
        @click="submit"
      >
        {{ t('common.save') }}
      </wui-btn>
    </template>
  </wui-modal>
</template>

<script setup lang="ts">
import { closeDialogKey, safeInject, WuiBtn, WuiInput, WuiModal } from '@wui/common-library'
import { computed, ref } from 'vue'

import { useLocalization } from '@/shared/composables/localization'

import { TextFormatLinkDialogResult } from '../lib/types'

const props = defineProps<{
  initialText?: string
  initialUrl?: string
  isEditing?: boolean
}>()

const { t } = useLocalization()
const close = safeInject<(result: TextFormatLinkDialogResult | false) => void>(closeDialogKey)

const linkText = ref(props.initialText ?? '')
const linkUrl = ref(props.initialUrl ?? '')

const modalClass = computed(() => (
  props.isEditing ? '!w-[480px] max-w-[90vw]' : 'w-105! max-w-[90vw]'
))

const canSubmit = computed(() => (
  linkUrl.value.trim().length > 0 && linkText.value.trim().length > 0
))

const submit = () => {
  if (!canSubmit.value) return

  close({
    url: linkUrl.value.trim(),
    text: linkText.value.trim(),
  })
}

const removeLink = () => {
  close({ remove: true })
}

const onTextEnter = () => {
  if (canSubmit.value) {
    submit()
  }
}
</script>
